<template>
  <div class="form-container">
    <v-card class="mx-auto" :title="i18n.global.t('pages.firmwareList.form.title.create')">
      <v-container>
        <v-file-input
          v-model="firmwareFile"
          accept=".tar"
          :label="i18n.global.t('pages.firmwareList.form.label.firmware')"
          ref="firmware-file"
          @change="validateFirmwareFile"
          :error-messages="errors.firmware_file"
        />
        <div class="placement-adjustment"></div>

        <MultipleDeviceTypeAutoComplete
          v-model="selectedDeviceTypes"
          @blur="validateDeviceTypes"
          :error-messages="errors.device_type"
        />
        <div class="placement-adjustment"></div>

        <v-text-field
          v-model="version"
          color="primary"
          :label="i18n.global.t('pages.firmwareList.version')"
          @blur="validateVersion"
          :error-messages="errors.version"
        />
        <div class="placement-adjustment"></div>

        <v-textarea
          :label="i18n.global.t('pages.firmwareList.form.label.memo')"
          v-model="memo"
          @blur="validateMemo"
          :error-messages="errors.memo"
        />
        <div class="placement-adjustment"></div>
      </v-container>
      <v-divider class="my-4"></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn class="ma-2 reset-btn" @click="$emit('close-modal')">
          {{ i18n.global.t('pages.common.button.close') }}
        </v-btn>
        <v-btn
          class="ma-2 exec-btn"
          @click="validateAndSendForm"
          :style="{
            backgroundColor: creating ? '#B3B8BB' : '',
          }"
        >
          {{ createBtnText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, ComputedRef } from 'vue';
import MultipleDeviceTypeAutoComplete from '@/components/AutoCompletes/MultipleDeviceTypeAutoComplete.vue';
import { isDeviceTypesValide, isMemoValide } from '@/services/validators';
import deviceType from '@/types/DeviceType';
import i18n from '@/lang/i18n';
import { firstFile } from '@/services/functions';

const props = defineProps<{
  creating: boolean;
}>();

const emit = defineEmits(['close-modal', 'send-form']);

const firmwareFile = ref<File | File[] | null | undefined>(undefined);
const memo = ref<string>('');
const version = ref<string>('');
const selectedDeviceTypes = ref<deviceType[]>([]);
const errors = ref<{
  device_type: Array<string>;
  version: Array<string>;
  firmware_file: Array<string>;
  memo: Array<string>;
}>({
  device_type: [],
  version: [],
  firmware_file: [],
  memo: [],
});

const isValide: ComputedRef<boolean> = computed(() => {
  return Object.values(errors.value).every((errors) => errors.length === 0);
});

const createBtnText: ComputedRef<string> = computed(() => {
  return props.creating ?
    i18n.global.t('pages.firmwareList.form.button.saving') :
    i18n.global.t('common.saveBtn');
});

const validateAndSendForm = (): void => {
  if (props.creating) {
    return;
  }
  validateForm();
  if (isValide.value) {
    sendForm();
  }
};

const validateForm = (): void => {
  validateDeviceTypes();
  validateVersion();
  validateFirmwareFile();
  validateMemo();
};

const validateDeviceTypes = (): void => {
  const errorMessage = isDeviceTypesValide(selectedDeviceTypes.value);
  if (errorMessage === null) {
    errors.value.device_type = [];
    return;
  }
  errors.value.device_type = [errorMessage];
};

const validateVersion = (): void => {
  const errorMessage = getVersionValidErrorMessage();
  if (errorMessage === null) {
    errors.value.version = [];
    return;
  }
  errors.value.version = [errorMessage];
};

const validateFirmwareFile = (): void => {
  const errorMessage = getFirmwareFileValidErrorMessage();
  if (errorMessage === null) {
    errors.value.firmware_file = [];
    return;
  }
  errors.value.firmware_file = [errorMessage];
};

const validateMemo = (): void => {
  let errorMessage = isMemoValide(memo.value);
  if (errorMessage === null) {
    errors.value.memo = [];
    return;
  }
  errors.value.memo = [errorMessage];
};

const sendForm = (): void => {
  const file = firstFile(firmwareFile.value);
  if (!file) {
    return;
  }

  emit('send-form', {
    payload: {
      memo: memo.value,
      ar_device_type_ids: selectedDeviceTypes.value.map(
        (deviceType) => deviceType.id
      ),
      version: version.value,
    },
    firmwareFile: file,
  });
};

const getVersionValidErrorMessage = (): string | null => {
  if (version.value.length < 1) {
    return i18n.global.t('pages.firmwareList.form.validate.versionEmpty');
  } else {
    const matches = new RegExp(/^[\w\d.\-_]+$/).exec(version.value);
    if (!matches || matches.length < 1) {
      return i18n.global.t('pages.firmwareList.form.validate.format');
    }
    if (version.value.length > 128) {
      return i18n.global.t('pages.firmwareList.form.validate.versionOverLenght');
    }
  }
  return null;
};

const getFirmwareFileValidErrorMessage = (): string | null => {
  if (!firstFile(firmwareFile.value)) {
    return i18n.global.t('pages.firmwareList.form.validate.noAttached');
  }
  return null;
};
</script>
<style scoped>
.form-container {
  padding: 15px;
}

.form-link .v-field__input {
  margin-top: 0;
  padding-top: 0;
}

.form-link .v-label {
  position: initial;
  margin-bottom: -5px;
}

.placement-adjustment {
  height: 15px;
}

.attachment-error {
  padding-left: 40px;
}
</style>
